import { useEffect } from "react";
import { createPortal } from "react-dom";
import { RefreshCw, ChevronRight, X } from "lucide-react";
import { NUM } from "@/lib/format";
import { cn } from "@/lib/utils";

const ChangeText = ({ x }) => {
  if (x.kind === "added") return <span className="shrink-0 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 text-[11px] font-bold tnum">Yeni +{NUM(x.to)}</span>;
  if (x.kind === "removed") return <span className="shrink-0 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 text-[11px] font-bold">Çıkarıldı</span>;
  const up = Number(x.delta) > 0;
  return (
    <span className="shrink-0 flex items-center gap-1.5 tnum text-[11.5px]">
      <span className="text-muted-foreground"><s>{NUM(x.from)}</s> → <b className="text-foreground">{NUM(x.to)}</b></span>
      <span className={cn("rounded-full px-1.5 text-[11px] font-bold border", up ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200")}>{up ? "+" : "−"}{NUM(Math.abs(Number(x.delta) || 0))}</span>
    </span>
  );
};

// Canlı sepet değişikliği: sessiz, küçük köşe bildirimi (kalem listede turuncu işaretli kalır, 15 sn sonra kendiliğinden kapanır)
export function LiveChangeAlert({ items, onShow, onDismiss }) {
  const n = items?.length || 0;
  useEffect(() => {
    if (!n) return undefined;
    const t = setTimeout(onDismiss, 15000);
    return () => clearTimeout(t);
  }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!n) return null;
  const shown = items.slice(-3);
  return createPortal((
    <div role="status" className="fixed z-[61] bottom-3 right-3 left-3 sm:left-auto sm:w-[360px] rounded-xl border border-orange-200 bg-card shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="prep-change-alert">
      <div className="flex items-center gap-2 pl-3 pr-1.5 py-1.5 border-b">
        <RefreshCw className="h-3.5 w-3.5 text-orange-600 shrink-0" />
        <span className="text-[12.5px] font-bold flex-1">Canlı sepet güncellendi</span>
        <span className="text-[11px] font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2 tnum" data-testid="prep-change-alert-count">{n}</span>
        <button type="button" onClick={onDismiss} className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-muted" aria-label="Kapat" data-testid="prep-change-alert-dismiss"><X className="h-4 w-4" /></button>
      </div>
      <ul className="divide-y">
        {shown.map((x, i) => (
          <li key={x.sig} data-testid={`prep-change-alert-item-${i}`}>
            <button type="button" onClick={() => { onShow(x.listId); onDismiss(); }} className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-muted/50" data-testid={`prep-change-alert-show-${i}`}>
              <div className="min-w-0 flex-1 leading-tight">
                <div className="text-[12.5px] font-semibold truncate" title={x.name}>{x.name}</div>
                <div className="text-[10.5px] text-muted-foreground truncate">{x.customer || "Perakende müşteri"}{x.owner ? ` · ${x.owner}` : ""}</div>
              </div>
              <ChangeText x={x} />
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            </button>
          </li>
        ))}
      </ul>
      {n > 3 && <div className="px-3 py-1 text-[10.5px] text-muted-foreground border-t">+{n - 3} değişiklik daha · listede turuncu işaretli</div>}
    </div>
  ), document.body);
}
